import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import { listTmAssignments } from "../api/tmTasks";
import type { TmAssignment } from "../api/tmTasks";
import { useAuth } from "../lib/auth/useAuth";
import { getWorkerUrl } from "../lib/session";
import { assignmentState, type TaskAssignments } from "../lib/team";

/** Assignations RACI par tâche TM native. N requêtes cachées 60s — pas
 * d'endpoint batch côté TM à ce jour, GET /tasks ne filtre pas par assigné. */
export function useTmAssignmentsBatch(
  taskIds: string[],
): Record<string, TaskAssignments<TmAssignment>> {
  const results = useQueries({
    queries: taskIds.map((id) => ({
      queryKey: ["tasks", "assignments", id],
      queryFn: () => listTmAssignments(id),
      staleTime: 60_000,
    })),
  });

  // Calcul direct à chaque render : listes courtes, map triviale — un useMemo
  // n'apporterait rien face au coût des deps dynamiques.
  const out: Record<string, TaskAssignments<TmAssignment>> = {};
  results.forEach((r, i) => {
    const id = taskIds[i];
    if (!id) return;
    out[id] = {
      items: (r.data?.items ?? []).filter((a) => a.active),
      state: assignmentState(r),
    };
  });
  return out;
}

/** Rôle RACI du user courant par tâche TM native (design : badge violet,
 * task_assignments.role — tâches natives uniquement).
 * actor_ref est un TEXT opaque (worker_url ou username) : on matche contre
 * l'ensemble des refs connues du user (claims OIDC + worker_url), même
 * approche que l'app Todo (AssignedScreen). */
export function useMyRaciRoles(
  taskIds: string[],
): Record<string, TmAssignment["role"]> {
  const { user } = useAuth();
  const byTask = useTmAssignmentsBatch(taskIds);

  const myRefs = useMemo(() => {
    const p = user?.profile as Record<string, unknown> | undefined;
    const set = new Set(
      [p?.sub, p?.preferred_username, p?.email].filter(
        (x): x is string => typeof x === "string",
      ),
    );
    try {
      set.add(getWorkerUrl(user));
    } catch {
      // Non authentifié : match sur les seuls claims.
    }
    return set;
  }, [user]);

  const out: Record<string, TmAssignment["role"]> = {};
  for (const [id, entry] of Object.entries(byTask)) {
    const mine = entry.items.find((a) => myRefs.has(a.actor_ref));
    if (mine) out[id] = mine.role;
  }
  return out;
}
